import AudioPlayer from './audio_player';
import WordTimingsService from './word_timings_service'
import audioStorageService from './audio_storage_service'
import { usesSegmentedReadingAid } from '../../reading_aids/reading_aid_languages'

class ChatMessageService {
  constructor() {
    this.audioChunks = []; // Array of audio chunks to play
    // Rate the worker asks this reply to be played at. Set for TTS models
    // with no speed setting (Eleven v4), which the client slows or speeds.
    this.streamPlaybackRate = null;
    this.dialogueAlignment = null;
    this.wordTimings;
    this.wordTimingsService = new WordTimingsService();
    this.audioPlayer = new AudioPlayer();
    this.fullMessage = null;
    this.chatMessageElement;
    this.chatElement;
    this.wordElements;
    this.activeWord;
    this.activeSentence = null;
    this.sentenceBoundaries = null;
    this.player;
    this.animationFrameId = null;
    this.highlightingMode = 'sentence';
    this.speedOptions = {
      "slow": 0.8,
      "standard": 1.0,
      "fast": 1.5,
    }
    this.chatMessageId = null;
    this.messageData = null;
    this.textOnly = false;
    this.allAudioReceived = false;
    this.fullMessageReceived = false;
    this.soundPlaying = false;
    this.playButton;
    this.audioPlayerService;
    this.retryCount = 0;
    this.language = null;
    this.receivedChatMessageId = null;
    this.messageState = {
      currentMessageId: null,
      hasMessageData: false,
      hasAudio: false,
      isDisplayed: false,
    };
    // Store spoken text (sanitized) for word alignment with non-Latin languages
    this.spokenText = null;
    this.ttsContext = null;
  }

  processAudio(data) {
    if (data.playbackRate) {
      this.streamPlaybackRate = data.playbackRate;
    }

    // Handle Cartesia's direct word timings
    if (data.wordTimings) {
      const messageLanguage = this.messageData && this.messageData.language ? this.messageData.language : this.language;
      if (!usesSegmentedReadingAid(messageLanguage)) {
        this.processWordTimings(data.wordTimings);
      }
    }
    // Handle ElevenLabs character-level timings (keep existing)
    else if (data.normalizedAlignment !== undefined && data.normalizedAlignment !== null) {
      const messageLanguage = this.messageData && this.messageData.language ? this.messageData.language : this.language;
      if (!usesSegmentedReadingAid(messageLanguage)) {
        if (data.playbackRate) {
          this.appendDialogueAlignment(data.normalizedAlignment);
        } else {
          this.processNormalizedAlignment(data.normalizedAlignment);
        }
      }
    } else {
      console.log('No word timings or normalizedAlignment found in audio data');
    }

    // Process audio based on type
    if (data.audioType && data.audioType.includes('mp3')) {
      this.processMp3Audio(data.audio);
    } else if (data.audioType && data.audioType.includes('pcm')) {
      this.processPcmAudio(data.audio, data.audioType);
    }
  }

  processPcmAudio(audioData, audioType) {
    // Extract sample rate from audioType (e.g., "pcm_16000" or "pcm_44100")
    let sampleRate = 16000; // Default fallback matches backend Cartesia rate
    
    if (audioType && audioType.includes('_')) {
      const parts = audioType.split('_');
      if (parts.length > 1) {
        const parsedRate = parseInt(parts[1]);
        if (!isNaN(parsedRate)) {
          sampleRate = parsedRate;
        }
      }
    }
    
    const pcmData = window.atob(audioData)

    // Convert the binary string to a Uint8Array
    const byteArray = new Uint8Array(pcmData.length);
    for (let i = 0; i < pcmData.length; i++) {
      byteArray[i] = pcmData.charCodeAt(i);
    }

    // Convert the Uint8Array to an ArrayBuffer
    const arrayBuffer = byteArray.buffer;

    // Create an AudioContext
    const audioContext = this.audioPlayer.audioContext;

    // Create an AudioBuffer
    const audioBuffer = audioContext.createBuffer(1, arrayBuffer.byteLength / 2, sampleRate);

    // Fill the AudioBuffer with the PCM data
    const channelData = audioBuffer.getChannelData(0);
    const dataView = new DataView(arrayBuffer);
    for (let i = 0; i < arrayBuffer.byteLength / 2; i++) {
      // Get the 16-bit sample at the current position, divide by 32768 to normalize to -1.0 - 1.0
      channelData[i] = dataView.getInt16(i * 2, true) / 32768;
    }

    // Store with metadata to distinguish PCM from MP3
    this.audioChunks.push({
      type: 'pcm',
      buffer: audioBuffer,
      sampleRate: sampleRate
    });
  }

  processMp3Audio(audioData) {
    const mp3Data = window.atob(audioData);
    // Store with metadata to distinguish MP3 from PCM
    this.audioChunks.push({
      type: 'mp3',
      data: mp3Data
    });
  }

  appendDialogueAlignment(chunk) {
    // Dialogue alignment can split a word across chunks. Keep characters until
    // playback, then convert once; chunk-local conversion would drop that word.
    if (!this.dialogueAlignment) {
      this.dialogueAlignment = { chars: [], charStartTimesMs: [], charDurationsMs: [] };
    }
    const alignment = this.dialogueAlignment;
    const last = alignment.charStartTimesMs.length - 1;
    const offset = last >= 0
      ? alignment.charStartTimesMs[last] + alignment.charDurationsMs[last]
      : 0;
    alignment.chars.push(...chunk.chars);
    for (const start of chunk.charStartTimesMs) alignment.charStartTimesMs.push(start + offset);
    alignment.charDurationsMs.push(...chunk.charDurationsMs);
  }

  processNormalizedAlignment(normalizedAlignment) {
    // Check if alignment data is valid
    if (!normalizedAlignment || !normalizedAlignment.chars || !normalizedAlignment.charStartTimesMs || !normalizedAlignment.charDurationsMs) {
      console.warn('Invalid normalized alignment data');
      return;
    }
    
    let wordTimings = null;

    let wordIndex = 0;
    let wordStartTime = normalizedAlignment.charStartTimesMs[0];
    let wordDuration = 0;

    let words = [];
    let wordStartTimesMs = [];
    let wordDurationsMs = [];

    normalizedAlignment.chars.forEach((char, i) => {
      wordDuration += normalizedAlignment.charDurationsMs[i];

      if ([' ', ',', '.', null].includes(char)) {
        const word = normalizedAlignment.chars.slice(wordIndex, i).join('');
        if (word !== '') {
          words.push(word);
          wordStartTimesMs.push(wordStartTime);
          wordDurationsMs.push(wordDuration);
        }

        wordIndex = i + 1;

        if (i + 1 < normalizedAlignment.charStartTimesMs.length) {
          wordStartTime = normalizedAlignment.charStartTimesMs[i + 1];
        }

        wordDuration = 0;
      }
    });

    const punctuationRegex = /[^\w\s]/g;
    const filteredWords = words.map(word => word.replace(punctuationRegex, ''));

    if (this.wordTimings === undefined || this.wordTimings === null) {
      wordTimings = {
        words: filteredWords,
        word_start_times_ms: wordStartTimesMs,
        word_durations_ms: wordDurationsMs
      };
          } else {
      const previousLastWordStartTimeMs = this.wordTimings.word_start_times_ms.slice(-1)[0];
      const previousWordDurationMs = this.wordTimings.word_durations_ms.slice(-1)[0];

      const offset = previousLastWordStartTimeMs + previousWordDurationMs;

      wordStartTimesMs = wordStartTimesMs.map(time => time + offset);

      wordTimings = {
        words: this.wordTimings.words.concat(filteredWords),
        word_start_times_ms: this.wordTimings.word_start_times_ms.concat(wordStartTimesMs),
        word_durations_ms: this.wordTimings.word_durations_ms.concat(wordDurationsMs)
      };
          }

    this.wordTimings = wordTimings;
  }

  // New method to handle Cartesia's direct word-level timestamps
  // New method to handle separate timestamp messages from Cartesia
  processTimestampMessage(data) {
    if (data.wordTimings) {
      const messageLanguage = this.messageData && this.messageData.language ? this.messageData.language : this.language;
      if (!usesSegmentedReadingAid(messageLanguage)) {
        this.processWordTimings(data.wordTimings);
      }
    }
  }

  processWordTimings(wordTimings) {
    if (this.wordTimings === undefined || this.wordTimings === null) {
      this.wordTimings = wordTimings;
    } else {
      // Cartesia sends absolute timings, not relative - just concatenate without offset
      // Each chunk already has the correct absolute timing values
      this.wordTimings = {
        words: this.wordTimings.words.concat(wordTimings.words),
        word_start_times_ms: this.wordTimings.word_start_times_ms.concat(wordTimings.word_start_times_ms),
        word_durations_ms: this.wordTimings.word_durations_ms.concat(wordTimings.word_durations_ms)
      };
    }
  }

  setupPlayer(url, soundEl, playbackRate = 1.0) {

    this.player = new Howl({
      src: [url],
      format: ['mp3'],
      onplay: () => {
        this.soundPlaying = true;
        if (soundEl) {
          soundEl.classList.add('d-none');
        }
      },
      onend: () => {
        this.soundPlaying = false;
      },
      rate: playbackRate
    });

    this.playButton = soundEl.parentNode.querySelector('.js-pause-chat-audio-btn');

    this.player.on('play', () => {
      this.playButton.classList.remove('d-none')
      this.playButton.innerHTML = '<i class="fa fa-pause"></i>'
    });

    this.player.on('pause', () => {
      this.playButton.innerHTML = '<i class="fa fa-play"></i>'
    })

    this.player.on('end', () => {
      // this.playButton.classList.add('d-none')
      this.playButton.innerHTML = '<i class="fa fa-play"></i>'
    })

    // this.playButton.addEventListener('click', () => {
    //   this.togglePlayer();
    // });
  }

  togglePlayer() {
    if (this.player.playing()) {
      this.player.pause();
    } else {
      this.player.play();
    }
  }

  reset() {
    this.audioChunks = [];
    this.streamPlaybackRate = null;
    this.dialogueAlignment = null;
    this.wordTimings = null;
    this.sentenceBoundaries = null;
    this.chatMessageElement = null;

    if (this.wordElements) {
      for (let word of this.wordElements) {
        word.style.backgroundColor = "";
        word.style.color = "";  // Reset the color when the word is no longer active.
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    try {
      if (this.activeWord !== null && this.activeWord !== undefined) {
        this.activeWord.style.backgroundColor = "";
        this.activeWord.style.color = "";  // Reset the color when the word is no longer active.
        this.activeWord.style.transition = "";
        this.activeWord.style.borderRadius = "";
        this.activeWord = null;
      }
    } catch (error) {
      console.error('Error resetting active word:', error);
    }

    this.activeSentence = null;

    // this.player = null;
    this.animationFrameId = null;
    this.chatMessageId = null;
    this.messageData = null;
    this.textOnly = false;
    this.fullMessage = null;
    this.allAudioReceived = false;
    this.fullMessageReceived = false;
    this.soundPlaying = false;
    this.spokenText = null;  // Clear spoken text on reset
    this.ttsContext = null;  // Clear TTS context on reset
    // this.playButton = null;
  }

  setupSync() {
    this.player.on('play', () => {
      // Kickstart the animation loop
      this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
    });

    // Stop tracking when the audio is paused or ended
    this.player.on('pause', () => {
      cancelAnimationFrame(this.animationFrameId);
    });

    this.player.on('end', () => {
      cancelAnimationFrame(this.animationFrameId);
      this.reset();
    });

    this.player.on('playerror', (error) => {
      console.error('Error playing audio:', error);
      if (this.retryCount < 3) {
        this.retryCount += 1;
        this.player.once('unlock', function () {
          this.player.play();
        });
      }
      cancelAnimationFrame(this.animationFrameId);
      this.reset();
    });

    this.player.on('loaderror', (error) => {
      console.error('Error playing audio:', error);
      cancelAnimationFrame(this.animationFrameId);
      this.reset();
    });
  }

  updateActiveWord(currentTime) {
    // Skip highlighting if mode is off
    if (this.highlightingMode === 'off') {
      return false;
    }

    if (this.highlightingMode === 'sentence') {
      return this.updateActiveSentence(currentTime);
    }

    // Original word highlighting logic
    if (this.activeWord !== null && this.activeWord !== undefined) {
      const wordStartTime = parseFloat(this.activeWord.dataset.startTime);
      const wordEndTime = parseFloat(this.activeWord.dataset.endTime);
      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        return true;
      }
    }

    for (let word of this.wordElements) {
      const wordStartTime = parseFloat(word.dataset.startTime);
      const wordEndTime = parseFloat(word.dataset.endTime);

      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        if (this.activeWord && this.activeWord.id === word.id) {
          return true;
        }

        if (this.activeWord !== null && this.activeWord !== undefined) {
          this.activeWord.style.backgroundColor = "";
          this.activeWord.style.color = "";
          this.activeWord.style.transition = "";
        }

        this.activeWord = word;

        // Don't apply highlighting styles if the word has challenge class (audio-only mode)
        if (!word.classList.contains('challenge')) {
          this.activeWord.style.backgroundColor = "#E5ECF4";
          this.activeWord.style.color = "#333";
          this.activeWord.style.transition = "none";
        } else {
          // For challenge mode, just add a subtle background that doesn't reveal the text
          this.activeWord.style.backgroundColor = "rgba(61, 71, 82, 0.3)";
          this.activeWord.style.transition = "none";
        }

        break;
      } else {
        word.style.backgroundColor = "";
        word.style.color = "";  // Reset the color when the word is no longer active.
        word.style.transition = "";
      }
    }
  }

  handleTimeUpdate() {
    if (this.player === null) {
      return;
    }

    const currentTime = this.player.seek();
    this.updateActiveWord(currentTime);

    // Continue the animation loop
    this.animationFrameId = requestAnimationFrame(this.handleTimeUpdate.bind(this));
  }

  setupWordElements() {
    const wordTimings = this.wordTimings;
    this.wordElements = Array.from(this.chatMessageElement.querySelectorAll('.js-translatable-text.word'));
    this.sentenceBoundaries = null; // Reset sentence boundaries when word elements change

    // Check if wordTimings exists and has the expected structure
    if (!wordTimings || !wordTimings.words || !Array.isArray(wordTimings.words)) {
      console.warn('Word timings not available or invalid structure');
      return;
    }

    // For silent transliteration languages (Hindi, Russian, Korean, Greek), we strip transliterations
    // before TTS. The spokenText differs from display text, so we need special alignment.
    // The backend only sends spokenText/ttsContext for these languages.
    const hasSilentTransliterations = this.ttsContext &&
      this.ttsContext.topicCategory === 'guided' &&
      this.spokenText &&
      this.isSilentTransliterationLanguage(this.ttsContext.language);

    console.log('setupWordElements:', {
      hasSilentTransliterations,
      hasSpokenText: !!this.spokenText,
      language: this.ttsContext?.language,
      wordTimingsWords: wordTimings?.words?.length,
      wordElementsCount: this.wordElements?.length
    });

    if (hasSilentTransliterations) {
      // Match word timings based on spoken (sanitized) text to display elements
      // The spoken text has transliterations stripped, but display text still has them
      this.setupWordElementsWithSpokenText(wordTimings);
    } else {
      // Standard 1:1 mapping for Latin languages
      for (let i = 0; i < wordTimings.words.length; i++) {
        const startTime = wordTimings.word_start_times_ms[i];
        const duration = wordTimings.word_durations_ms[i];

        const wordElement = this.wordElements[i];
        if (wordElement) {
          wordElement.dataset.startTime = startTime / 1000; // Set data-start-time attribute in seconds
          wordElement.dataset.endTime = (startTime + duration) / 1000; // Set data-end-time attribute in seconds
          wordElement.id = `word-${i}`;
        }
      }
    }
  }

  /**
   * Check if language uses non-Latin script
   */
  isNonLatinLanguage(language) {
    const nonLatinLanguages = [
      'japanese', 'chinese', 'russian', 'greek', 'korean',
      'arabic', 'thai', 'hindi', 'hebrew'
    ];
    return language && nonLatinLanguages.includes(language.toLowerCase());
  }

  /**
   * Check if language has silent transliterations (stripped before TTS)
   * These languages show transliterations on screen but don't speak them
   */
  isSilentTransliterationLanguage(language) {
    const silentTransliterationLanguages = ['russian', 'korean', 'hindi', 'greek'];
    return language && silentTransliterationLanguages.includes(language.toLowerCase());
  }

  /**
   * Setup word elements with spoken text alignment for non-Latin guided courses
   * Word timings are based on sanitized text (no transliterations)
   * Display elements include transliterations in parentheses
   */
  setupWordElementsWithSpokenText(wordTimings) {
    // Get words from display elements (includes transliterations)
    const displayWords = this.wordElements.map(el => {
      const text = el.textContent || el.innerText || '';
      return text.trim();
    });

    // Get words from spoken text (no transliterations)
    const spokenWords = wordTimings.words;

    let spokenIdx = 0;
    let displayIdx = 0;
    let insideTransliteration = false;  // Track if we're inside a (...) block

    while (displayIdx < displayWords.length) {
      const displayWord = displayWords[displayIdx];
      const wordElement = this.wordElements[displayIdx];

      // Check if this word starts a transliteration block
      if (displayWord.startsWith('(')) {
        insideTransliteration = true;
      }

      // Check if this display word is part of a transliteration
      const isTransliteration = insideTransliteration;

      // Check if this word ends a transliteration block
      if (displayWord.endsWith(')')) {
        insideTransliteration = false;
      }

      if (isTransliteration) {
        // Skip transliteration words - they weren't spoken
        // Give them zero duration at the previous spoken word's end time
        if (wordElement) {
          const prevEndTime = spokenIdx > 0
            ? (wordTimings.word_start_times_ms[spokenIdx - 1] + wordTimings.word_durations_ms[spokenIdx - 1]) / 1000
            : 0;
          wordElement.dataset.startTime = prevEndTime;
          wordElement.dataset.endTime = prevEndTime;
          wordElement.id = `word-${displayIdx}`;
        }
        displayIdx++;
        continue;
      }

      // This is a real word - apply timing if we have spoken words left
      if (spokenIdx < spokenWords.length) {
        if (wordElement) {
          const startTime = wordTimings.word_start_times_ms[spokenIdx];
          const duration = wordTimings.word_durations_ms[spokenIdx];
          wordElement.dataset.startTime = startTime / 1000;
          wordElement.dataset.endTime = (startTime + duration) / 1000;
          wordElement.id = `word-${displayIdx}`;
        }
        spokenIdx++;
      } else {
        // No more spoken words - give remaining display words the last end time
        const lastEndTime = spokenWords.length > 0
          ? (wordTimings.word_start_times_ms[spokenWords.length - 1] + wordTimings.word_durations_ms[spokenWords.length - 1]) / 1000
          : 0;
        if (wordElement) {
          wordElement.dataset.startTime = lastEndTime;
          wordElement.dataset.endTime = lastEndTime;
          wordElement.id = `word-${displayIdx}`;
        }
      }

      displayIdx++;
    }
  }

  setHighlightingMode(mode) {
    this.highlightingMode = mode;

    // Clear any existing highlighting when switching to off mode
    if (mode === 'off') {
      this.clearAllHighlighting();
    }
  }

  getHighlightingMode() {
    return this.highlightingMode;
  }

  clearAllHighlighting() {
    // Clear word highlighting
    if (this.wordElements) {
      for (let word of this.wordElements) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Clear active word
    if (this.activeWord !== null && this.activeWord !== undefined) {
      this.activeWord.style.backgroundColor = "";
      this.activeWord.style.color = "";
      this.activeWord.style.transition = "";
      this.activeWord.style.borderRadius = "";
      this.activeWord = null;
    }

    // Clear active sentence
    if (this.activeSentence) {
      for (let word of this.activeSentence) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
      this.activeSentence = null;
    }
  }

  findSentenceBoundaries(wordElements) {
    const sentences = [];
    let currentSentence = [];
    const endOfSentenceRegex = /[.!?。！？។៕។…\u3002\uFF01\uFF1F\u0964\u0965]/;

    for (let i = 0; i < wordElements.length; i++) {
      const word = wordElements[i];
      currentSentence.push(word);

      // Check if this word ends with sentence-ending punctuation
      const wordText = word.textContent || word.innerText || '';
      if (endOfSentenceRegex.test(wordText)) {
        sentences.push([...currentSentence]);
        currentSentence = [];
      }
    }

    // Add any remaining words as the last sentence
    if (currentSentence.length > 0) {
      sentences.push(currentSentence);
    }

    return sentences;
  }

  getSentenceContainingWord(word) {
    if (!this.sentenceBoundaries) {
      this.sentenceBoundaries = this.findSentenceBoundaries(this.wordElements);
    }

    for (const sentence of this.sentenceBoundaries) {
      if (sentence.includes(word)) {
        return sentence;
      }
    }

    return [word]; // Fallback to single word if no sentence found
  }

  async playAudio(voiceSpeed) {
    if (this.dialogueAlignment) {
      const alignment = this.dialogueAlignment;
      const last = alignment.charStartTimesMs.length - 1;
      // A zero-duration separator also flushes a final word without punctuation.
      alignment.chars.push(' ');
      alignment.charStartTimesMs.push(last >= 0 ? alignment.charStartTimesMs[last] + alignment.charDurationsMs[last] : 0);
      alignment.charDurationsMs.push(0);
      this.wordTimings = null;
      this.processNormalizedAlignment(alignment);
      this.dialogueAlignment = null;
    }
    const streamPlaybackRate = this.streamPlaybackRate;
    this.streamPlaybackRate = null;
    const playbackRate = streamPlaybackRate || this.speedOptions[voiceSpeed];
    let audioBlob, audioUrl;
    let audioBtn, slowAudioBtn;
    
    // Check if we have PCM or MP3 data
    const firstChunk = this.audioChunks[0];
    
    if (firstChunk && firstChunk.type === 'pcm') {
      // Handle PCM audio chunks
      const audioContext = this.audioPlayer.audioContext;
      const sampleRate = firstChunk.sampleRate;
      
      // Calculate total length
      let totalLength = 0;
      for (const chunk of this.audioChunks) {
        if (chunk.type === 'pcm') {
          totalLength += chunk.buffer.length;
        }
      }
      
      // Create a single buffer to hold all audio
      const combinedBuffer = audioContext.createBuffer(1, totalLength, sampleRate);
      let offset = 0;
      const channelData = combinedBuffer.getChannelData(0);
      
      for (const chunk of this.audioChunks) {
        if (chunk.type === 'pcm') {
          const sourceData = chunk.buffer.getChannelData(0);
          channelData.set(sourceData, offset);
          offset += sourceData.length;
        }
      }
      
      // Convert AudioBuffer to WAV format for Howler.js
      audioBlob = await this.audioBufferToWav(combinedBuffer);
      audioUrl = URL.createObjectURL(audioBlob);
      
    } else {
      // Handle MP3 audio chunks (existing code)
      const combinedAudioData = this.audioChunks
        .map(chunk => chunk.data || chunk) // Handle both old and new format
        .join("");
      const byteArray = new Uint8Array(combinedAudioData.length);
      
      for (let i = 0; i < combinedAudioData.length; i++) {
        byteArray[i] = combinedAudioData.charCodeAt(i);
      }
      audioBlob = new Blob([byteArray.buffer], { type: 'audio/mp3' });
      audioUrl = URL.createObjectURL(audioBlob);
    }

    // find closest js-play-chat-audio-btn to chatMessageElement
    if (this.chatMessageElement !== undefined && this.chatMessageElement !== null) {
      audioBtn = this.chatMessageElement.querySelector('.js-play-chat-audio-btn, .js-cartus-message-speech:not([data-playback-rate])');
      slowAudioBtn = this.chatMessageElement.querySelector('.js-slow-audio-btn, .js-cartus-message-speech[data-playback-rate]');
      if (audioBtn !== undefined && audioBtn !== null) {
        // Replays read this, so they keep the reply's speed.
        if (streamPlaybackRate) {
          audioBtn.dataset.playbackRate = streamPlaybackRate;
        }
        if (this.chatMessageId !== null) {
          audioStorageService.setAudioUrl(this.chatMessageId, audioUrl);
          audioBtn.dataset.audioKey = audioStorageService.getStorageKey(this.chatMessageId);
        }
      }
    }

    this.audioChunks = [];
    // TODO: Remove this
    // current sync code - words eventually get out of sync
    // audioBtn.dataset.wordTimings = JSON.stringify(this.wordTimings);
    // this.audioPlayerService.setupWordElements(this.wordTimings, this.chatMessageElement);
    // this.audioPlayerService.setupPlayer(audioUrl, audioBtn, playbackRate);
    // this.audioPlayerService.setupSync();
    // this.audioPlayerService.player.play();
    // return this.audioPlayerService.player;

    // TODO: Remove this
    // Old sync - working better sync
    // this.setupWordElements();
    // this.setupPlayer(audioUrl, audioBtn, playbackRate);
    // this.setupSync();
    // this.player.play();
    // return this.player;

    // Todo: Mix and match approch that syncs better
    // Only set word timings for non-CJK languages
    if (!usesSegmentedReadingAid(this.language)) {
      audioBtn.dataset.wordTimings = JSON.stringify(this.wordTimings);
      if (slowAudioBtn) {
        slowAudioBtn.dataset.wordTimings = JSON.stringify(this.wordTimings);
      }
      this.setupWordElements();

    }

    this.audioPlayerService.setupPlayer(audioUrl, audioBtn, playbackRate);
    this.player = this.audioPlayerService.player;

    // Only setup sync for non-CJK languages
    if (!usesSegmentedReadingAid(this.language)) {
      this.setupSync();
    }

    this.player.play();
    return this.player;
  }

  addMessage(messageData) {
    this.messageData = messageData;
    this.fullMessage = messageData['full_message'];
    this.fullMessageReceived = true;
    this.messageState.currentMessageId = messageData['chat_message_id'];
    this.messageState.hasMessageData = true;
    this.messageState.isDisplayed = false;
      // Only process if this is a new message or we're in text-only mode
    if (this.audioMessageReady() || this.textOnlyMessageReady()) {
      this.emitMessageAdded(messageData['chat_message_id']);
      this.checkMessageReady();
    }
  }

  showMessage() {

    // Only show if we have valid message data and it matches our current message ID
    if (!this.messageData || this.messageData['chat_message_id'] !== this.messageState.currentMessageId) {
      console.log('Skipping showMessage - message data mismatch or missing');
      return;
    }

    if (this.messageState.isDisplayed) {
      console.log('Skipping showMessage - message already displayed');
      return;
    }

    if (this.messageData['data']) {
      this.chatElement.insertAdjacentHTML('beforeend', this.messageData['data']);
      this.chatMessageElement = this.chatElement.querySelector(`#chat-message-${this.messageData['chat_message_id']}`);
      this.messageState.isDisplayed = true;
      this.emitMessageShown(this.messageData['chat_message_id']);
    }
  }

  checkMessageReady() {
    if (this.textOnlyMessageReady() || this.audioMessageReady()) {
      this.emitMessageReady(this.messageData['chat_message_id']);
      this.allAudioReceived = false;
    }
  }

  textOnlyMessageReady() {
    return this.textOnly && this.fullMessageReceived;
  }

  audioMessageReady() {
    return this.allAudioReceived && this.fullMessageReceived;
  }

  updateAudioReceived(messageId) {
    // Only process audio for the current message
    this.allAudioReceived = true;
    this.messageState.hasAudio = true;
    if (messageId === this.messageState.currentMessageId) {
      this.checkMessageReady();
    }
  }

  emitMessageReady(messageId) {
    const event = new CustomEvent('chat:messageready', { detail: { messageId: messageId } });
    document.dispatchEvent(event);
  }

  emitMessageAdded(messageId) {
    const event = new CustomEvent('chat:messageadded', { detail: { messageId: messageId } });
    document.dispatchEvent(event);
  }

  emitMessageShown(messageId) {
    const event = new CustomEvent('chat:messageshown', { detail: { messageId: messageId } });
    document.dispatchEvent(event);
  }

  showTranslation(messageData) {
    if (this.chatMessageElement === undefined || this.chatMessageElement === null) {
      console.log('chatMessageElement not found');
      return;
    }

    const chatMessageElement = this.chatMessageElement.querySelector(`#chat-message-${messageData['id']}`);
    const chatMessageContentEl = chatMessageElement.querySelector('.chat-message-content');

    if (chatMessageContentEl === undefined || chatMessageContentEl === null) {
      console.log('chatMessageContentEl not found');
      return;
    }

    if (messageData['data']) {
      chatMessageContentEl.insertAdjacentHTML('beforeend', messageData['data']);
    }
    console.log('finish showing translation')
  }

  updateActiveSentence(currentTime) {
    // Find the current word based on timing
    let currentWord = null;
    for (let word of this.wordElements) {
      const wordStartTime = parseFloat(word.dataset.startTime);
      const wordEndTime = parseFloat(word.dataset.endTime);

      if (currentTime >= wordStartTime && currentTime <= wordEndTime) {
        currentWord = word;
        break;
      }
    }

    if (!currentWord) {
      return false;
    }

    // Get the sentence containing the current word
    const currentSentenceWords = this.getSentenceContainingWord(currentWord);

    // Check if we're already highlighting the correct sentence
    if (this.activeSentence && this.activeSentence.length === currentSentenceWords.length &&
      this.activeSentence.every(word => currentSentenceWords.includes(word))) {
      return true;
    }

    // Clear previous sentence highlighting
    if (this.activeSentence) {
      for (let word of this.activeSentence) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Clear all other words
    for (let word of this.wordElements) {
      if (!currentSentenceWords.includes(word)) {
        word.style.backgroundColor = "";
        word.style.color = "";
        word.style.transition = "";
        word.style.borderRadius = "";
      }
    }

    // Highlight the new sentence with proper border radius
    this.activeSentence = currentSentenceWords;
    for (let i = 0; i < this.activeSentence.length; i++) {
      const word = this.activeSentence[i];

      // Don't apply highlighting styles if the word has challenge class (audio-only mode)
      if (!word.classList.contains('challenge')) {
        word.style.backgroundColor = "#E5ECF4";
        word.style.color = "#333";
        word.style.transition = "none";
      } else {
        // For challenge mode, just add a subtle background that doesn't reveal the text
        word.style.backgroundColor = "rgba(61, 71, 82, 0.3)";
        word.style.transition = "none";
      }

      // Apply border radius based on position in sentence
      if (this.activeSentence.length === 1) {
        // Single word sentence - rounded on both sides
        word.style.borderRadius = "4px";
      } else if (i === 0) {
        // First word - rounded left only
        word.style.borderRadius = "4px 0 0 4px";
      } else if (i === this.activeSentence.length - 1) {
        // Last word - rounded right only
        word.style.borderRadius = "0 4px 4px 0";
      } else {
        // Middle words - no rounded edges
        word.style.borderRadius = "0";
      }
    }

    // Keep track of the active word for other functionality
    this.activeWord = currentWord;

    return true;
  }

  // Helper method to convert AudioBuffer to WAV blob for Howler.js
  audioBufferToWav(buffer) {
    const length = buffer.length * buffer.numberOfChannels * 2 + 44;
    const arrayBuffer = new ArrayBuffer(length);
    const view = new DataView(arrayBuffer);
    const channels = [];
    let offset = 0;
    let pos = 0;
    
    // Write WAV header
    const setUint16 = (data) => {
      view.setUint16(pos, data, true);
      pos += 2;
    };
    const setUint32 = (data) => {
      view.setUint32(pos, data, true);
      pos += 4;
    };
    
    // RIFF identifier
    setUint32(0x46464952);
    // file length minus RIFF identifier length and file description length
    setUint32(length - 8);
    // RIFF type
    setUint32(0x45564157);
    // format chunk identifier
    setUint32(0x20746d66);
    // format chunk length
    setUint32(16);
    // sample format (raw)
    setUint16(1);
    // channel count
    setUint16(buffer.numberOfChannels);
    // sample rate
    setUint32(buffer.sampleRate);
    // byte rate (sample rate * block align)
    setUint32(buffer.sampleRate * 2 * buffer.numberOfChannels);
    // block align (channel count * bytes per sample)
    setUint16(buffer.numberOfChannels * 2);
    // bits per sample
    setUint16(16);
    // data chunk identifier
    setUint32(0x61746164);
    // data chunk length
    setUint32(length - pos - 4);
    
    // Write interleaved PCM samples
    const channelData = buffer.getChannelData(0);
    const len = channelData.length;
    
    for (let i = 0; i < len; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(pos, sample * 0x7FFF, true);
      pos += 2;
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  }

}

export default ChatMessageService;
